import React, { useRef, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { device } from "../../../atoms/Utlis/media";
import {
  MessageCircle,
  Plus,
  WifiOff,
  Search,
  Mail,
  Phone,
  Sparkles,
  UserPlus,
} from "lucide-react";

import { useConversations } from "../../../../hooks/useConversations";
import ConversationList from "../../../molecules/Dashboard/WhatsApp/ConversationList";
import MessageBubble from "../../../atoms/Dashboard/WhatsApp/MessageBubble";
import ChatInput from "../../../molecules/Dashboard/WhatsApp/ChatInput";
import BackButtonIconPng from "../../../../assets/Icons/SingleAlbum/back.png";
import NewConversationModal from "../../../molecules/Dashboard/WhatsApp/NewConversationModal";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getHostUrl } from "../../../../Redux/ApiCalls/getHostUrl";
import { receiveNewMessage } from "../../../../Redux/Slice/Dashboard/WhatsAppSlice";
import TemplateFallbackModal from "../../../molecules/Dashboard/WhatsApp/TemplateFallbackModal";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import AddLeadModal from "../Leads/AddLeadModal";

const formatGroupDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const ConversationsPage: React.FC = () => {
  const {
    conversations,
    activeConversation,
    conversationsLoading,
    messages,
    messagesLoading,
    sendingMessage,
    error,
    isError,
    selectConversation,
    sendMessage,
    refresh,
    loadMoreMessages,
    messagesHasMore,
  } = useConversations();

  const processedConversations = React.useMemo(() => {
    const uniqueMap = new Map<string, (typeof conversations)[0]>();

    const sortedAsc = [...conversations].sort((a, b) => {
      const timeA = new Date(a.updated_at || a.created_at).getTime();
      const timeB = new Date(b.updated_at || b.created_at).getTime();
      return timeA - timeB;
    });

    for (const c of sortedAsc) {
      if (c.customer_number) {
        uniqueMap.set(c.customer_number, c);
      }
    }

    return Array.from(uniqueMap.values()).sort((a, b) => {
      const timeA = new Date(a.updated_at || a.created_at).getTime();
      const timeB = new Date(b.updated_at || b.created_at).getTime();
      return timeB - timeA;
    });
  }, [conversations]);

  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [failedMessageText, setFailedMessageText] = useState("");
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);

  const filteredConversations = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return processedConversations;
    }
    const query = searchQuery.toLowerCase().trim();
    return processedConversations.filter((c) => {
      const name = (c.customer?.name || "").toLowerCase();
      const phone = (c.customer?.phone || "").toLowerCase();
      const customerNum = (c.customer_number || "").toLowerCase();
      const integratedNum = (c.integrated_number || "").toLowerCase();
      return (
        name.includes(query) ||
        phone.includes(query) ||
        customerNum.includes(query) ||
        integratedNum.includes(query)
      );
    });
  }, [processedConversations, searchQuery]);

  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const dispatch = useAppDispatch();
  const token =
    useAppSelector((state) => state.auth.access_token) ||
    localStorage.getItem("access_token");
  const wsRef = useRef<WebSocket | null>(null);
  const activeCustomerNumberRef = useRef<string | null>(null);
  activeCustomerNumberRef.current = activeConversation?.customer_number || null;

  useEffect(() => {
    if (!token) return;

    const hostUrl = getHostUrl();
    const wsProto = hostUrl.startsWith("https") ? "wss" : "ws";
    const rawHost = hostUrl.replace(/^https?:\/\//, "");
    const wsUrl = `${wsProto}://${rawHost}/ws/whatsapp/?token=${encodeURIComponent(token)}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      if (activeCustomerNumberRef.current) {
        ws.send(
          JSON.stringify({
            action: "subscribe_customer",
            customer_number: activeCustomerNumberRef.current,
          })
        );
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.event === "message.new" && msg.data) {
          dispatch(receiveNewMessage(msg.data));
          refresh();
        }
      } catch {
        // ignore malformed websocket payloads
      }
    };

    ws.onclose = () => { };

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [token, dispatch, refresh]);

  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && activeConversation?.customer_number) {
      ws.send(
        JSON.stringify({
          action: "subscribe_customer",
          customer_number: activeConversation.customer_number,
        })
      );
    }
  }, [activeConversation?.customer_number]);

  const [shouldAdjustScroll, setShouldAdjustScroll] = useState(false);
  const prevScrollHeightRef = useRef<number>(0);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el || messagesLoading || !messagesHasMore) return;

    if (el.scrollTop < 10) {
      prevScrollHeightRef.current = el.scrollHeight;
      setShouldAdjustScroll(true);
      loadMoreMessages();
    }
  }, [loadMoreMessages, messagesLoading, messagesHasMore]);

  const prevMessagesLengthRef = useRef(messages.length);
  useEffect(() => {
    const el = messagesAreaRef.current;
    if (el) {
      if (shouldAdjustScroll) {
        el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
        setShouldAdjustScroll(false);
      } else if (messages.length > prevMessagesLengthRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, shouldAdjustScroll]);

  useEffect(() => {
    const el = messagesAreaRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [activeConversation?.id]);

  const handleSendMessage = (text: string) => {
    const lastInboundMessage = [...messages]
      .reverse()
      .find((m) => m.direction === "inbound");

    let isWindowOpen = false;
    if (lastInboundMessage) {
      isWindowOpen =
        new Date().getTime() - new Date(lastInboundMessage.created_at).getTime() <
        24 * 60 * 60 * 1000;
    } else if (messagesLoading) {
      isWindowOpen =
        activeConversation?.is_window_open !== undefined
          ? Boolean(activeConversation.is_window_open)
          : activeConversation?.conversation_type === "user_session" && activeConversation?.window_expiry
            ? new Date() < new Date(activeConversation.window_expiry)
            : false;
    } else {
      isWindowOpen = false;
    }

    if (!isWindowOpen) {
      setFailedMessageText(text);
      setShowTemplateModal(true);
      return;
    }

    sendMessage(text).catch((err: any) => {
      const errMsg = err?.message || "";
      if (
        errMsg.toLowerCase().includes("24h") ||
        errMsg.toLowerCase().includes("24 hour") ||
        errMsg.toLowerCase().includes("window") ||
        errMsg.toLowerCase().includes("template")
      ) {
        setFailedMessageText(text);
        setShowTemplateModal(true);
      } else {
        showErrorToast(errMsg || "Failed to send message");
      }
    });
  };

  const handleSendTemplateConfirm = async (
    customerName: string,
    category: "marketing" | "utility" | "authentication"
  ) => {
    setSendingTemplate(true);
    try {
      await sendMessage(failedMessageText, {
        message_type: "template",
        customer_name: customerName,
        template_category: category,
      });
      showSuccessToast("Template message sent successfully!");
      setShowTemplateModal(false);
      setFailedMessageText("");
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to send template message");
    } finally {
      setSendingTemplate(false);
    }
  };

  const handleNewChatSuccess = () => {
    refresh();
  };

  const contact = activeConversation?.customer;
  const displayName =
    contact?.name?.trim() || activeConversation?.customer_number || "";
  const displayPhone =
    contact?.phone?.trim() || activeConversation?.customer_number || "";
  const avatarInitials = getInitials(displayName);

  const isWindowOpen = React.useMemo(() => {
    const lastInboundMessage = [...messages]
      .reverse()
      .find((m) => m.direction === "inbound");

    if (lastInboundMessage) {
      return (
        new Date().getTime() - new Date(lastInboundMessage.created_at).getTime() <
        24 * 60 * 60 * 1000
      );
    }
    if (messagesLoading) {
      return activeConversation?.is_window_open !== undefined
        ? Boolean(activeConversation.is_window_open)
        : activeConversation?.conversation_type === "user_session" && activeConversation?.window_expiry
          ? new Date() < new Date(activeConversation.window_expiry)
          : false;
    }
    return false;
  }, [
    messages,
    messagesLoading,
    activeConversation?.is_window_open,
    activeConversation?.conversation_type,
    activeConversation?.window_expiry,
  ]);

  const unreadTotal = processedConversations.reduce(
    (sum, c) => sum + (c.unread_count ?? 0),
    0
  );

  return (
    <PageWrapper>
      <PageHeader>
        <HeaderLeft>
          <BackLink to="/dashboard/whatsapp" aria-label="Go back">
            <img
              src={BackButtonIconPng}
              style={{ width: "19.6px", height: "16.8px" }}
              alt="Back"
            />
          </BackLink>
          <HeaderText>
            <HeaderBadge>
              <Sparkles size={13} />
              Inbox
            </HeaderBadge>
            <PageTitle>Conversations</PageTitle>
            <PageSubtitle>Chat with customers in real time</PageSubtitle>
          </HeaderText>
        </HeaderLeft>
        <HeaderStats>
          <HeaderStat>
            <HeaderStatValue>{processedConversations.length}</HeaderStatValue>
            <HeaderStatLabel>Chats</HeaderStatLabel>
          </HeaderStat>
          {unreadTotal > 0 && (
            <HeaderStat $accent>
              <HeaderStatValue>{unreadTotal > 99 ? "99+" : unreadTotal}</HeaderStatValue>
              <HeaderStatLabel>Unread</HeaderStatLabel>
            </HeaderStat>
          )}
        </HeaderStats>
      </PageHeader>

      {!isOnline && (
        <OfflineBanner>
          <WifiOff size={18} />
          No internet connection. Please connect to continue chatting.
        </OfflineBanner>
      )}

      <Container>
        <Sidebar>
          <SidebarHeader>
            <SidebarHeaderTop>
              <SidebarTitle>
                <MessageCircle size={18} />
                All chats
              </SidebarTitle>
              <ChatCountPill>{filteredConversations.length}</ChatCountPill>
            </SidebarHeaderTop>
            <NewChatButton onClick={() => setShowNewChatModal(true)}>
              <Plus size={16} />
              New chat
            </NewChatButton>
            <SearchContainer>
              <SearchIconWrapper>
                <Search size={17} />
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder="Search by name or number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </SidebarHeader>

          {isError && error?.conversations && (
            <SidebarError>{error.conversations}</SidebarError>
          )}

          <ConversationList
            conversations={filteredConversations}
            activeId={activeConversation?.id ?? null}
            onSelect={selectConversation}
            loading={conversationsLoading}
          />
        </Sidebar>

        <ChatPanel>
          {!activeConversation ? (
            <EmptyChat>
              <EmptyChatGlow />
              <EmptyChatIcon>
                <MessageCircle size={34} strokeWidth={1.8} />
              </EmptyChatIcon>
              <EmptyChatTitle>Select a conversation</EmptyChatTitle>
              <EmptyChatDesc>
                Pick a contact from the list to view messages and reply instantly.
              </EmptyChatDesc>
            </EmptyChat>
          ) : (
            <>
              <ChatHeader>
                <ChatHeaderAvatar>{avatarInitials}</ChatHeaderAvatar>
                <ChatHeaderInfo>
                  <ChatHeaderName>{displayName}</ChatHeaderName>
                  {displayPhone && displayPhone !== displayName && (
                    <ChatHeaderMeta>
                      <Phone size={12} />
                      {displayPhone}
                    </ChatHeaderMeta>
                  )}
                </ChatHeaderInfo>
                <WindowPill $open={isWindowOpen}>
                  <WindowDot $open={isWindowOpen} />
                  {isWindowOpen ? "24h window open" : "Template only"}
                </WindowPill>
              </ChatHeader>

              <MessagesArea ref={messagesAreaRef} onScroll={handleMessagesScroll}>
                {messagesLoading && messages.length === 0 ? (
                  <LoadingMessages>Loading messages...</LoadingMessages>
                ) : isError && error?.messages ? (
                  <NoMessages>{error.messages}</NoMessages>
                ) : messages.length === 0 ? (
                  <NoMessages>
                    <NoMessagesIcon>
                      <MessageCircle size={22} />
                    </NoMessagesIcon>
                    No messages yet. Say hello!
                  </NoMessages>
                ) : (
                  (() => {
                    let lastDateLabel = "";
                    return messages.map((msg) => {
                      const dateLabel = formatGroupDate(msg.created_at);
                      const showSeparator = dateLabel !== lastDateLabel;
                      lastDateLabel = dateLabel;

                      return (
                        <React.Fragment key={msg.id}>
                          {showSeparator && (
                            <DateSeparatorWrapper>
                              <DateSeparatorBadge>{dateLabel}</DateSeparatorBadge>
                            </DateSeparatorWrapper>
                          )}
                          <MessageBubble message={msg} />
                        </React.Fragment>
                      );
                    });
                  })()
                )}
              </MessagesArea>

              <ChatInput
                onSend={handleSendMessage}
                disabled={!activeConversation || sendingMessage || !isOnline}
                placeholder={
                  !isOnline
                    ? "Offline — reconnect to send"
                    : sendingMessage
                      ? "Sending..."
                      : isWindowOpen
                        ? "Type a message..."
                        : "Window closed — template required"
                }
              />
            </>
          )}
        </ChatPanel>

        {activeConversation && (
          <DetailsSidebar>
            <DetailsHeaderCard>
              <DetailsAvatar>{avatarInitials}</DetailsAvatar>
              <DetailsInfoWrapper>
                <DetailsName>
                  {contact?.name?.trim() || "Unregistered user"}
                </DetailsName>
                <DetailsPhoneHeader>{displayPhone}</DetailsPhoneHeader>
              </DetailsInfoWrapper>
            </DetailsHeaderCard>

            <DetailsSectionTitle>Contact details</DetailsSectionTitle>
            <DetailsSection>
              <DetailItemCard>
                <DetailIconWrap>
                  <Mail size={16} />
                </DetailIconWrap>
                <DetailItemContent>
                  <DetailLabel>Email</DetailLabel>
                  <DetailText>
                    {contact?.email?.trim() || "Not registered"}
                  </DetailText>
                </DetailItemContent>
              </DetailItemCard>

              <DetailItemCard>
                <DetailIconWrap>
                  <Phone size={16} />
                </DetailIconWrap>
                <DetailItemContent>
                  <DetailLabel>Phone</DetailLabel>
                  <DetailText>{displayPhone || "Not registered"}</DetailText>
                </DetailItemContent>
              </DetailItemCard>
            </DetailsSection>

            <AddLeadButton onClick={() => setAddLeadModalOpen(true)}>
              <UserPlus size={16} />
              Add as lead
            </AddLeadButton>
          </DetailsSidebar>
        )}
      </Container>

      {showNewChatModal && (
        <NewConversationModal
          onClose={() => setShowNewChatModal(false)}
          onSuccess={handleNewChatSuccess}
        />
      )}
      {showTemplateModal && (
        <TemplateFallbackModal
          onClose={() => setShowTemplateModal(false)}
          onConfirm={handleSendTemplateConfirm}
          defaultCustomerName={displayName}
          messageText={failedMessageText}
          submitting={sendingTemplate}
        />
      )}
      <AddLeadModal
        open={addLeadModalOpen}
        onClose={() => setAddLeadModalOpen(false)}
        customerNumber={activeConversation?.customer_number ?? undefined}
        onSuccess={() => {
          setAddLeadModalOpen(false);
          showSuccessToast("Lead created successfully!");
        }}
      />
    </PageWrapper>
  );
};

export default ConversationsPage;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const OfflineBanner = styled.div`
  background: linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%);
  color: #991b1b;
  border: 1px solid #fecaca;
  padding: 12px 18px;
  border-radius: 14px;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  box-shadow: 0 6px 18px rgba(239, 68, 68, 0.08);
`;

const PageWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 24px;
  animation: ${fadeUp} 0.35s ease-out;
  @media ${device.tablet} {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding: 20px 22px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(37, 211, 102, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  @media ${device.tablet} {
    flex-direction: column;
    padding: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const HeaderBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 26px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
  @media ${device.tablet} {
    font-size: 22px;
  }
`;

const PageSubtitle = styled.p`
  margin: 6px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
`;

const HeaderStats = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`;

const HeaderStat = styled.div<{ $accent?: boolean }>`
  min-width: 76px;
  padding: 10px 14px;
  border-radius: 14px;
  text-align: center;
  background: ${(p) =>
    p.$accent ? "rgba(37, 211, 102, 0.12)" : "rgba(255, 255, 255, 0.82)"};
  border: 1px solid
    ${(p) => (p.$accent ? "rgba(37, 211, 102, 0.22)" : "rgba(0, 0, 0, 0.06)")};
`;

const HeaderStatValue = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1;
`;

const HeaderStatLabel = styled.div`
  margin-top: 4px;
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  @media ${device.tablet} {
    flex-direction: column;
    min-height: 520px;
  }
`;

const Sidebar = styled.div`
  width: 340px;
  min-width: 300px;
  background: #fff;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  @media ${device.tablet} {
    width: 100%;
    min-width: 0;
    max-height: 42vh;
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const SidebarHeader = styled.div`
  padding: 18px 16px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 201, 81, 0.06) 0%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const SidebarHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
`;

const SidebarTitle = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChatCountPill = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  font-family: "Urbanist", sans-serif;
  font-size: 12px;
  font-weight: 700;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
  &:hover {
    background: #fff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const NewChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 12px;
  height: 42px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.95) 0%,
    rgba(206, 86, 176, 0.95) 100%
  );
  color: #ffffff;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.22);
  transition: all 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.3);
  }
`;

const ChatPanel = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #eef2f6;
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 14px;
  backdrop-filter: blur(8px);
`;

const ChatHeaderAvatar = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 800;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
`;

const ChatHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatHeaderName = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatHeaderMeta = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.58);
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const WindowPill = styled.div<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 999px;
  font-family: "Urbanist", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => (p.$open ? "#15803d" : "#9a3412")};
  background: ${(p) =>
    p.$open ? "rgba(37, 211, 102, 0.12)" : "rgba(249, 115, 22, 0.12)"};
  border: 1px solid
    ${(p) =>
    p.$open ? "rgba(37, 211, 102, 0.22)" : "rgba(249, 115, 22, 0.22)"};
  flex-shrink: 0;
  @media ${device.tablet} {
    display: none;
  }
`;

const WindowDot = styled.span<{ $open: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => (p.$open ? "#22c55e" : "#f97316")};
  box-shadow: 0 0 0 3px
    ${(p) =>
    p.$open ? "rgba(34, 197, 94, 0.18)" : "rgba(249, 115, 22, 0.18)"};
  animation: ${(p) => (p.$open ? pulse : "none")} 2s ease-in-out infinite;
`;

const MessagesArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
  overscroll-behavior: contain;
  /* Cool neutral wallpaper — distinct from peach title / soft sent bubbles */
  background-color: #e8edf3;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
  background-size: 28px 28px;
  @media ${device.tablet} {
    min-height: 300px;
  }
`;

const NoMessages = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  text-align: center;
  padding: 32px 24px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const NoMessagesIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  color: #c94daa;
`;

const LoadingMessages = styled(NoMessages)`
  color: #9ca3af;
`;

const SidebarError = styled.div`
  margin: 12px 16px 0;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fef2f2;
  color: #dc2626;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 600;
`;

const EmptyChat = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  overflow: hidden;
`;

const EmptyChatGlow = styled.div`
  position: absolute;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(201, 77, 170, 0.12) 0%,
    transparent 70%
  );
`;

const EmptyChatIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #c94daa;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.2) 0%,
    rgba(206, 86, 176, 0.18) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.14);
  box-shadow: 0 10px 28px rgba(201, 77, 170, 0.12);
`;

const EmptyChatTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  font-family: "Urbanist", sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
`;

const EmptyChatDesc = styled.p`
  position: relative;
  z-index: 1;
  margin: 0;
  max-width: 320px;
  text-align: center;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  line-height: 1.45;
`;

const DateSeparatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 14px 0 6px;
  width: 100%;
`;

const DateSeparatorBadge = styled.span`
  background: rgba(255, 255, 255, 0.92);
  color: #64748b;
  font-family: "Urbanist", sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  letter-spacing: 0.03em;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 11px 16px 11px 40px;
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  color: #171717;
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    background: #fff;
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DetailsSidebar = styled.div`
  width: 320px;
  min-width: 280px;
  background: #fff;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  padding: 20px 18px;
  overflow-y: auto;
  flex-shrink: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 201, 81, 0.04) 0%,
    #ffffff 120px
  );
  @media ${device.desktop} {
    width: 300px;
  }
  @media ${device.tablet} {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const DetailsHeaderCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
`;

const DetailsAvatar = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.85) 0%, rgba(206, 86, 176, 0.85) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 800;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.22);
`;

const DetailsInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const DetailsName = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailsPhoneHeader = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.52);
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailsSectionTitle = styled.h3`
  margin: 0 0 12px 4px;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 800;
  color: rgba(23, 23, 23, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
`;

const DetailItemCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
`;

const DetailIconWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
`;

const DetailItemContent = styled.div`
  min-width: 0;
  flex: 1;
`;

const DetailLabel = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.48);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
`;

const DetailText = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  word-break: break-all;
`;

const AddLeadButton = styled.button`
  width: 100%;
  margin-top: auto;
  padding: 12px 16px;
  border-radius: 12px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: linear-gradient(
    144.01deg,
    rgba(255, 201, 81, 0.95) 0%,
    rgba(206, 86, 176, 0.95) 100%
  );
  color: white;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.24);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(201, 77, 170, 0.32);
  }
`;
